import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { type ThemeColor, useWebviewBridge, useWebviewEvents, type WebviewTheme } from '@contexts/webview-bridge';

import { persistDarkModeChoice, readDarkModeChoice, reconcileDarkModeStores } from './dark-mode-preference';

// Typed with `#${ThemeColor}` so the constants stay in lock-step with the bridge
// union at compile time: any future hex change that drifts from `ThemeColor`
// becomes a tsc error here, not a silent runtime lie.
const LIGHT_THEME_COLOR: `#${ThemeColor}` = '#FFFFFF';
const DARK_THEME_COLOR: `#${ThemeColor}` = '#242426';

// Strip the `#` and uppercase to match the format mobivery expects. The cast
// is provably safe: the input is restricted to `#${ThemeColor}`, so the output
// is always a `ThemeColor` literal.
const toBridgeHex = (color: `#${ThemeColor}`): ThemeColor => color.replace('#', '').toUpperCase() as ThemeColor;

const updateThemeMeta = (isDark: boolean) => {
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = isDark ? DARK_THEME_COLOR : LIGHT_THEME_COLOR;
  }
};

export const DarkModeContext = createContext({
  isDarkMode: false,
  toggleDarkMode: (_e?: React.MouseEvent | React.KeyboardEvent) => {},
  isActive: false,
});

export const DarkModeProvider = ({ children }: { children: ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  // Launch theme pushed by native. Never persisted — a stored manual choice always
  // wins, and native re-resolves the OS theme on every launch.
  const [webviewInitialTheme, setWebviewInitialTheme] = useState<WebviewTheme | null>(null);
  const { isDarkModeEnabled: isActive, isWebView } = useContext(FeatureFlagContext);
  const { sendChangeThemeColor, sendRequestThemeColor, onSetInitialTheme } = useWebviewEvents();
  const { isInitialized: isBridgeInitialized } = useWebviewBridge();
  const initialThemeEmittedRef = useRef(false);
  const themeRequestedRef = useRef(false);

  // Bring the two stores into agreement: migrate a pre-existing cookie-only choice
  // into storage, and repair a cookie the WebView reverted. Leaving the cookie stale
  // would keep a value we distrust where anything else would trust it.
  //
  // Gated on the flag, and that gate is not cosmetic: with dark mode off the stored
  // choice is ignored entirely, so the repair buys this app nothing — while the
  // storage-to-cookie direction would still rewrite the `.xceed.me`-wide cookie from
  // this origin, clobbering a choice made on another xceed property for no gain.
  useEffect(() => {
    if (!isActive) {
      return;
    }
    reconcileDarkModeStores();
  }, [isActive]);

  const toggleDarkMode = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      const newDarkMode = !isDarkMode;

      const applyDarkMode = () => {
        setIsDarkMode(newDarkMode);

        if (newDarkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }

        updateThemeMeta(newDarkMode);
        // Fire-and-forget so the native shell repaints its system chrome and
        // persists the choice as the user preference.
        sendChangeThemeColor({
          color: toBridgeHex(newDarkMode ? DARK_THEME_COLOR : LIGHT_THEME_COLOR),
        }).catch((): void => undefined);

        persistDarkModeChoice(newDarkMode);
      };

      const prefersReducedMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!document.startViewTransition || prefersReducedMotion) {
        applyDarkMode();
        return;
      }

      if (e) {
        let x: number, y: number;

        if ('clientX' in e && e.clientX !== 0) {
          x = e.clientX;
          y = e.clientY;
        } else {
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          x = rect.left + rect.width / 2;
          y = rect.top + rect.height / 2;
        }

        document.documentElement.style.setProperty('--vt-x', `${x}px`);
        document.documentElement.style.setProperty('--vt-y', `${y}px`);
      }

      document.startViewTransition(applyDarkMode);
      // `sendChangeThemeColor` is listed rather than silenced: `useWebviewEvents`
      // memoizes it on the bridge's own stable `send`, so it does not churn the callback
      // — and with it the context value — on re-render.
    },
    [isDarkMode, sendChangeThemeColor]
  );

  const determineInitialDarkMode = () => {
    if (!isActive) {
      return false;
    }

    const storedChoice = readDarkModeChoice();
    if (storedChoice !== null) {
      return storedChoice;
    }

    // In the webview, `prefers-color-scheme` is unreliable (Android WebView),
    // so the OS theme arrives from native instead.
    if (webviewInitialTheme !== null) {
      return webviewInitialTheme === 'dark';
    }

    return globalThis.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const initialDarkMode = determineInitialDarkMode();

  // Sync the resolved initial theme (cookie / native launch theme / system
  // preference) into React state and the DOM. Runs on mount and whenever the
  // resolution changes.
  useEffect(() => {
    if (initialDarkMode !== isDarkMode) {
      setIsDarkMode(initialDarkMode);
      if (initialDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      updateThemeMeta(initialDarkMode);
    }
  }, [initialDarkMode, isDarkMode]);

  // Listen for native's launch theme before requesting it, so the reply can't
  // race the registration. Invalid payloads are ignored.
  // The handshake is strictly pull-based: this provider mounts behind the auth
  // render gate, so this listener may register long after BRIDGE_READY. Native
  // must only send SET_INITIAL_THEME in reply to REQUEST_THEME_COLOR — an
  // unprompted push could arrive before registration and be silently dropped
  // (the bridge dispatches to registered handlers only, there is no queue).
  useEffect(() => {
    if (!isWebView || !isBridgeInitialized) {
      return;
    }
    return onSetInitialTheme(({ theme }) => {
      if (theme !== 'light' && theme !== 'dark') {
        return;
      }
      setWebviewInitialTheme(theme);
    });
  }, [isWebView, isBridgeInitialized, onSetInitialTheme]);

  // Ask native for the launch theme once the bridge is ready. Shells that own
  // the launch resolution (stored manual choice, else device OS theme) reply
  // with SET_INITIAL_THEME; legacy shells ignore the request.
  useEffect(() => {
    if (!isActive || !isWebView || !isBridgeInitialized || themeRequestedRef.current) {
      return;
    }
    themeRequestedRef.current = true;
    sendRequestThemeColor().catch((): void => undefined);
  }, [isActive, isWebView, isBridgeInitialized, sendRequestThemeColor]);

  // Emit the launch theme once, for legacy shells that rely on the web pushing
  // it to paint their chrome. A stored-choice launch IS the user's persisted
  // manual choice, so it goes out unflagged — new shells repaint + persist it,
  // migrating the web preference to native on first launch. Only resolutions with
  // no stored choice are flagged `initial`, so shells that resolve the launch theme
  // themselves discard them instead of persisting an OS-derived guess. Manual
  // toggles emit unflagged inside `applyDarkMode` above.
  useEffect(() => {
    if (!isBridgeInitialized || initialThemeEmittedRef.current) {
      return;
    }
    initialThemeEmittedRef.current = true;
    const hasManualChoice = readDarkModeChoice() !== null;
    sendChangeThemeColor({
      color: toBridgeHex(initialDarkMode ? DARK_THEME_COLOR : LIGHT_THEME_COLOR),
      ...(hasManualChoice ? {} : { initial: true }),
    }).catch((): void => undefined);
  }, [isBridgeInitialized, initialDarkMode, sendChangeThemeColor]);

  // Repo rule, and Sonar S6481: a fresh object here re-renders every consumer of the
  // context on every render of this provider, which wraps the whole app.
  const value = useMemo(() => ({ isDarkMode, toggleDarkMode, isActive }), [isDarkMode, toggleDarkMode, isActive]);

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>;
};
